import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Badge, DataTable, clientSortRows } from "../ui";
import { MDBSpinner } from "mdb-react-ui-kit";
import { api } from "../../services";
import { getImageUrl } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NativeInput from "../NativeInput";

const CatalogItemModal = ({ show, setShow, catalogId, storeId, onClose }) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogStoreId, setCatalogStoreId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tableSortColumn, setTableSortColumn] = useState("product");
  const [sortDirection, setSortDirection] = useState("asc");
  const modalRef = useRef(null);

  const normalizeId = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object") return val._id || val.id || val.$oid || null;
    return null;
  };

  useEffect(() => {
    const normalized = normalizeId(storeId);
    if (normalized) {
      setCatalogStoreId(normalized);
    }
  }, [storeId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (show && modalRef.current && !modalRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, setShow]);

  useEffect(() => {
    const fetchData = async () => {
      if (!catalogId) return;

      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");

        const [catalogResponse, productsResponse] = await Promise.all([
          api.get(`/catalog/${catalogId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api.get("/products-dropdown", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const catalogData = catalogResponse.data.data;
        const productsData = productsResponse.data.items;
        const existingProductIds =
          catalogData.items?.map((item) => item.product?._id).filter(Boolean) || [];

        setProducts(productsData);
        setCatalogProducts(existingProductIds);

        const resolvedCatalogStoreId =
          normalizeId(catalogData?.store) ||
          normalizeId(catalogData?.storeId) ||
          normalizeId(catalogData?.store_id) ||
          null;

        setCatalogStoreId((prev) => prev || resolvedCatalogStoreId);
      } catch (err) {
        console.error("Failed to load data", err);
        toast.error(t("Failed to load products"));
      } finally {
        setIsLoading(false);
      }
    };

    if (show) {
      fetchData();
    }
  }, [catalogId, show, t]);

  const getStoreId = (item) =>
    normalizeId(item?.store) ||
    normalizeId(item?.storeId) ||
    normalizeId(item?.store_id) ||
    normalizeId(item?.category_id?.store) ||
    normalizeId(item?.category_id?.storeId) ||
    normalizeId(item?.category_id?.store_id);

  const availableProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products.filter((product) => {
      const matchesSearch =
        !term ||
        product.name?.en?.toLowerCase().includes(term) ||
        product.name?.ar?.toLowerCase().includes(term) ||
        String(product.sku || "")
          .toLowerCase()
          .includes(term);

      const notAlreadyInCatalog = !catalogProducts.includes(product._id);
      const matchesStore = catalogStoreId && getStoreId(product) === catalogStoreId;

      return matchesSearch && notAlreadyInCatalog && !!matchesStore;
    });
  }, [products, searchTerm, catalogProducts, catalogStoreId]);

  const sortedAvailableProducts = useMemo(
    () =>
      clientSortRows(availableProducts, tableSortColumn, sortDirection, (product, col) => {
        switch (col) {
          case "product":
            return (isArabic ? product.name?.ar : product.name?.en || "").toLowerCase();
          case "category":
            return (
              isArabic
                ? product?.category_id?.name?.ar
                : product?.category_id?.name?.en || ""
            ).toLowerCase();
          case "price":
            return product.price ?? 0;
          case "createdAt":
            return product.createdAt ? new Date(product.createdAt).getTime() : 0;
          default:
            return "";
        }
      }),
    [availableProducts, tableSortColumn, sortDirection, isArabic]
  );

  const emptyStateMessage = useMemo(() => {
    if (searchTerm.trim()) {
      return t("No available products match your search");
    }
    if (catalogProducts.length > 0) {
      return t("All products are already in this catalog");
    }
    return t("No products available");
  }, [searchTerm, catalogProducts.length, t]);

  const productColumns = useMemo(
    () => [
      {
        id: "product",
        header: t("product"),
        sortable: true,
        width: 260,
        render: (product) => (
          <div className="d-flex align-items-center gap-2">
            <img
              src={getImageUrl(product.image_path)}
              alt={product.name?.en || t("product")}
              className="rounded"
              style={{
                width: 40,
                height: 40,
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
            <div className="min-w-0">
              <div className="fw-semibold text-truncate">
                {isArabic ? product.name?.ar : product.name?.en}
              </div>
              <small className="text-muted text-nowrap">
                SKU: {product.sku || "N/A"}
              </small>
            </div>
          </div>
        ),
      },
      {
        id: "category",
        header: t("category"),
        sortable: true,
        width: 140,
        render: (product) => (
          <span className="text-nowrap">
            {isArabic
              ? product?.category_id?.name?.ar
              : product?.category_id?.name?.en || "-"}
          </span>
        ),
      },
      {
        id: "price",
        header: t("price"),
        sortable: true,
        width: 100,
        align: "end",
        render: (product) => (
          <span className="text-nowrap">{product.price ?? "-"}</span>
        ),
      },
      {
        id: "createdAt",
        header: t("createdAt"),
        sortable: true,
        width: 180,
        render: (product) =>
          product.createdAt ? (
            <div className="text-nowrap" dir="ltr">
              {new Date(product.createdAt).toLocaleDateString("en-US")}{" "}
              {new Date(product.createdAt).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </div>
          ) : (
            "-"
          ),
      },
    ],
    [t, isArabic]
  );

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error(t("No products selected to add"));
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        products: selectedProducts.map((productId) => ({
          productId,
          is_visible: true,
        })),
      };

      await api.post(`/v2.1/catalog/${catalogId}/bulk-items`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(`${selectedProducts.length} ${t("item(s) added to catalog!")}`);
      setShow(false);
      onClose();
    } catch (err) {
      console.error("Error adding items to catalog:", err);
      const errorMessage =
        err.response?.data?.message || t("Failed to add items to catalog");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!show) {
      setSelectedProducts([]);
      setSearchTerm("");
    }
  }, [show]);

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShow(false);
    }
  };

  return (
    <div
      className="modal fade show d-block oh-modal oh-modal--xl"
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalog-item-modal-title"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content" ref={modalRef}>
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2 flex-wrap" id="catalog-item-modal-title">
              {t("addproductstocatalog")}
              {selectedProducts.length > 0 && (
                <Badge color="primary">
                  {selectedProducts.length} {t("selected")}
                </Badge>
              )}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShow(false)}
              aria-label={t("cancel")}
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <NativeInput
                label={t("searchavailableproducts")}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("searchavailableproducts")}
              />
            </div>

            <DataTable
              tableId="catalog-add-products"
              showColumnSettings={false}
              hideToolbar
              hideFooter
              maxBodyHeight={420}
              selectable
              selectionMode="checkbox"
              selectedIds={selectedProducts}
              onSelectionChange={setSelectedProducts}
              columns={productColumns}
              data={sortedAvailableProducts}
              rowKey="_id"
              loading={isLoading}
              sort={{
                column: tableSortColumn,
                direction: sortDirection,
                onChange: ({ column, direction }) => {
                  setTableSortColumn(column);
                  setSortDirection(direction);
                },
              }}
              emptyState={
                <div className="py-3">
                  <div className="text-muted">{emptyStateMessage}</div>
                  {searchTerm.trim() ? (
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => setSearchTerm("")}
                      className="mt-2 px-0"
                    >
                      {t("Clear search")}
                    </Button>
                  ) : null}
                </div>
              }
              selectedLabel={t("selected")}
              actionDropdownLabel={t("action")}
              selectAllLabel={t("selectAll", { defaultValue: "Select All" })}
              isRtl={isArabic}
            />
          </div>

          <div className="modal-footer d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="text-muted small">
              {selectedProducts.length > 0
                ? `${selectedProducts.length} ${t("product selected")}`
                : null}
            </div>
            <div className="d-flex gap-2">
              <Button color="secondary" onClick={() => setShow(false)} disabled={isLoading}>
                {t("cancel")}
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                disabled={selectedProducts.length === 0 || isLoading}
              >
                {isLoading ? (
                  <>
                    <MDBSpinner size="sm" className="me-2" />
                    {t("saving")}
                  </>
                ) : (
                  `${t("add")} ${selectedProducts.length} ${t("items")}`
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogItemModal;
